import { Source } from '../../enums';
import { ItemDeletedResponse } from '../../responses';
import { ContactEntity, ContactType } from '../contact';
import { Entity, EntityLink, TimestampDetails } from '../entity';
import { Job, WorkOrder, WorkOrderStatus } from '../job';
import { Address, Lex, Location, SimpleGoogleRoute, SiteTiny } from '../shared';
import { AppointmentPro } from './appointment.pro';

export function getJobEntity(job: Job) {
    if (!job) return null;
    return { _id: job._id, name: job.jobNumber } as Entity;
}

export function getWorkOrderEntity(job: WorkOrder) {
    if (!job) return null;
    return { _id: job._id, name: job.workOrderNumber } as Entity;
}

export const UnallocatedUserId = '00000000-0000-0000-0000-1000000000';

export const UnallocatedUser: ContactEntity = {
    _id: UnallocatedUserId,
    name: '<Unassigned>',
    type: ContactType.System
};

export enum AllocationStatus {
    Open = 'Open',
    Enroute = 'Enroute',
    OnSite = 'Onsite',
    CheckedOut = 'Checked Out',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
    Overhead = 'Overhead'
}

export const AllocationStatuses = [
    AllocationStatus.Open,
    AllocationStatus.Enroute,
    AllocationStatus.OnSite,
    AllocationStatus.CheckedOut,
    AllocationStatus.Completed,
    AllocationStatus.Cancelled,
    AllocationStatus.Overhead
];

export enum AllocationType {
    PhoneCall = 'Phone Call',
    Meeting = 'Meeting',
    Onsite = 'Onsite'
}

export enum BookingStatus {
    Confirmed = 'Confirmed',
    Tentative = 'Tentative'
}

export enum AllocationReminderStatus {
    NotRequired = 'Not Required',
    Pending = 'Pending',
    Sent = 'Sent'
}

export type RunLifeCycle = 'Pre' | 'Post' | 'None';


export const AllocationBookingStatus = [BookingStatus.Confirmed, BookingStatus.Tentative];

export type AllocationStatusInfo = {
    reason?: string;
    on?: Date;
    by?: Entity;
    note?: string;
}

export type AllocationRule = {

    /** rule string, eg: RRULE:FREQ=WEEKLY;COUNT=30;INTERVAL=1;WKST=MO */
    _id: string;

    /** rule text, eg: every week for 30 times */
    name: string;

    start?: Date;
    end?: Date;
    exclude?: Date[];
}

export interface Allocation extends Entity, TimestampDetails {
    _id: string;
    active?: boolean;
    run: Entity;
    runOrder?: Lex;
    resource: ContactEntity;
    startDate: Date;
    endDate: Date;
    status: AllocationStatus;

    booking?: Entity;
    job?: Entity;
    workOrder?: Entity;

    opportunity?: Entity;

    site?: SiteTiny;
    overhead?: AppointmentPro;
    clusterColor?: string;
    note?: string;
    type?: AllocationType;

    dateCompleted?: Date;
    completedNote?: string;

    allocations?: OtherAllocation[];

    createdAt?: Date;
    updatedAt?: Date;
    createdBy?: Entity;
    updatedBy?: Entity;
    contractorResource?: ContactEntity;
    source: Source;

    resourceMigration?: any;
    notificationSent?: boolean;

    additionalDays?: number;
    bookingStatus?: BookingStatus;

    locked?: boolean;
    allDay?: boolean;

    /** TODO: [Question] This field isn't saved to DB, what it for? */
    timezone?: string;

    // handle create allocation for equipment
    groupId?: string;

    // Route Information
    estimatedDuration?: number;
    travelDistance?: number;
    travelDuration?: number;
    travelDistanceString?: string;
    travelDurationString?: string;

    policy?: Entity;
    reminderStatus?: AllocationReminderStatus;

    statusInfo?: AllocationStatusInfo;

    rule?: AllocationRule;
    parentId?: string;

    integrationId?: string;


    /** Not save to db: 
     * calculation field for displaying, 
     * if resource.scheduling.tags not exactly equal allocation.workOrder.schedulingTags */
    isConflict?: boolean;

    // Not save to db: 
    // This field is a flag to indicate that the allocation is created by selecting multiple slots from the calendar.
    selectedMultipleSlots?: boolean;


    /** @description virtual allocation for some-entity-type */
    virtualEntity?: EntityLink;
}

export interface AllocationWithMeta extends Allocation {
    meta: AllocationMeta;
}

export interface AllocationMeta {
    name?: string;
    type: Entity;
    priority: Entity;
    tags: Entity[];
    schedulingTags?: Entity[];
    customer: string;
    customerWO: string;
    category: Entity;
    status?: WorkOrderStatus;
    site: {
        _id: string;
        address: string;
        contactName: string;
        contactPhone: string;
    };

}

export interface AllocationCard extends Pick<
    Allocation,
    '_id'
    | 'resource'
    | 'startDate'
    | 'endDate'
    | 'status'
    | 'contractorResource'
    | 'overhead'
    | 'job'
    | 'workOrder'
    | 'opportunity'
    | 'run'
    | 'source'
    | 'note'
    | 'bookingStatus'
    | 'groupId'
    | 'locked'
    | 'rule'
    | 'parentId'
    | 'allDay'
    | 'isConflict'
    | 'virtualEntity'
> {
    meta?: AllocationMeta;
    metaOpportunity?: AllocationMeta;
    site?: SiteTiny;
}

export interface BasicAllocation {
    resource: string,
    startDate: string,
    endDate: string,
    note: string
}


export interface OtherAllocation {
    _id: string;
    resource: Entity;
    status: AllocationStatus;
    startDate: Date;
}

export interface AllocationSummary {
    _id: string;
    startDate: Date;
    endDate: Date;
    resource: Entity;
    canDelete?: boolean;
    status: AllocationStatus;
}

export interface AllocationPatch extends Partial<Pick<
    Allocation,
    '_id'
    | 'resource'
    | 'contractorResource'
    | 'startDate'
    | 'endDate'
    | 'note'
    | 'status'
    | 'updatedBy'
    | 'source'
    | 'notificationSent'
    | 'type'
    | 'bookingStatus'
    | 'locked'
    | 'allDay'
    | 'statusInfo'
    | 'rule'
    | 'overhead'
    | 'integrationId'
>> {
}


export interface AllocationBatchRequest {
    deleted: string[];
    modified: AllocationPatch[];
    new: Allocation[];
    portal?: boolean;
    runLifeCycle?: RunLifeCycle;
    canSendNotification?: boolean;
}

export interface AllocationBatchResponse {
    deleted: ItemDeletedResponse[];
    modified: Allocation[];
    new: Allocation[];
    success: boolean;
}

export interface AllocationTrackingContact {
    _id: string;
    name: string;
    phone?: string;
    mobile?: string;
    location: Location;
}

export interface AllocationTrackable {
    trackable: boolean;
    expired?: boolean;
    refresh?: boolean;
}

export interface AllocationTracking extends AllocationTrackable {
    estTimeMinutes: number;
    estDistance: string;
    jobNumber: string;
    workOrderNumber: string;
    customerWO: string;
    address: Address;
    resource: AllocationTrackingContact;
    message: string;
    status: AllocationStatus;
    logo?: string;
    route?: SimpleGoogleRoute;
}

